import { useCallback, useState } from "react"
import { useClient } from "urql"

import "../graphql/invoice.query"

import type {
  CheckInvoiceDuplicatesQuery,
  CheckInvoiceDuplicatesQueryVariables,
  Invoice,
  InvoiceDuplicateCheckInput,
} from "@/graphql/codegen/graphql"
import { CheckInvoiceDuplicatesDocument } from "@/graphql/codegen/graphql"
import { NotificationType } from "@/schemas/notification"
import { useNotificationsStore } from "@/stores/useNotificationsStore"

export type CheckInvoiceDuplicatesResult = { status: "success"; duplicates: Invoice[] } | { status: "failure" }

export interface UseCheckInvoiceDuplicatesReturn {
  checkInvoiceDuplicates: (input: InvoiceDuplicateCheckInput) => Promise<CheckInvoiceDuplicatesResult>
  isCheckingDuplicates: boolean
}

const FAILURE_MESSAGE = "Failed to check for duplicate invoices. Please try again."

export const useCheckInvoiceDuplicates = (): UseCheckInvoiceDuplicatesReturn => {
  const client = useClient()
  const notify = useNotificationsStore((state) => state.enqueueNotification)
  const [isCheckingDuplicates, setIsCheckingDuplicates] = useState(false)

  const checkInvoiceDuplicates = useCallback(
    async (input: InvoiceDuplicateCheckInput): Promise<CheckInvoiceDuplicatesResult> => {
      setIsCheckingDuplicates(true)
      try {
        const result = await client
          .query<
            CheckInvoiceDuplicatesQuery,
            CheckInvoiceDuplicatesQueryVariables
          >(CheckInvoiceDuplicatesDocument, { input }, { requestPolicy: "network-only" })
          .toPromise()

        // Treat any failure mode as a failure: transport/GraphQL error, or a
        // malformed response missing the expected data path. Without the latter
        // guard a partial response would silently look like "no duplicates" and
        // bypass the warning UX entirely.
        const duplicates = result.data?.invoice?.checkDuplicates?.duplicates as Invoice[] | undefined
        if (result.error || !duplicates) {
          notify({ type: NotificationType.Error, message: FAILURE_MESSAGE })
          return { status: "failure" }
        }

        return { status: "success", duplicates }
      } catch (err) {
        // Exchange-level throws don't surface as `result.error`; surface the
        // toast here too so the user always sees feedback on failure. Log so
        // BE/observability can trace what threw when investigating reports.
        console.error("checkInvoiceDuplicates failed:", err)
        notify({ type: NotificationType.Error, message: FAILURE_MESSAGE })
        return { status: "failure" }
      } finally {
        setIsCheckingDuplicates(false)
      }
    },
    [client, notify]
  )

  return { checkInvoiceDuplicates, isCheckingDuplicates }
}
